import { useRef, useState } from 'react';
import { DriveFolderView } from './DriveBrowser';
import type { DriveFile } from '../data/drive';
import SafeIcon from './SafeIcon';
import { useApp } from '../i18n/AppContext';
import { useUi } from '../store/ui';
import { useDemo, useWorkspaceData } from '../store/DemoStore';

export type PickedKind = 'Photo' | 'Video' | 'Audio' | 'Document';
export interface PickedItem {
  name: string;
  kind: PickedKind;
  src?: string;
  source: 'Upload' | 'Material bank' | 'Drive';
  file: string;
}

const MAX_DEMO_PREVIEW_BYTES = 900 * 1024;
const DEFAULT_DRIVE_FILE_SIZE_MB = 5;
function kindFromMimeType(mimeType: string): PickedKind {
  if (mimeType.startsWith('image')) return 'Photo';
  if (mimeType.startsWith('video')) return 'Video';
  if (mimeType.startsWith('audio')) return 'Audio';
  return 'Document';
}
const withoutExtension = (fileName: string) => fileName.replace(/\.[^.]+$/, '');
const pickedItemKey = (item: PickedItem) => `${item.source}:${item.file}`;
const iconForKind: Record<PickedKind, string> = {
  Photo: 'FiImage',
  Video: 'FiFilm',
  Audio: 'FiMusic',
  Document: 'FiFileText',
};

export default function MaterialPicker({
  title,
  multiple = false,
  kinds,
  onCancel,
  onPick,
}: {
  title: string;
  multiple?: boolean;
  kinds?: PickedKind[];
  onCancel: () => void;
  onPick: (items: PickedItem[]) => void;
}) {
  const { translate } = useApp();
  const { route } = useUi();
  const { commands } = useDemo();
  const { assets, drive } = useWorkspaceData(route.workspaceId);
  const [tab, setTab] = useState<'upload' | 'bank' | 'drive'>('bank');
  const [picked, setPicked] = useState<PickedItem[]>([]);
  const [error, setError] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const driveSizes = useRef<Record<string, number>>({});
  const isAllowedKind = (kind: PickedKind) => !kinds || kinds.includes(kind);

  const materialBankItems: PickedItem[] = assets
    .filter((asset) => !asset.archived)
    .map((asset) => ({
      name: asset.name,
      kind: asset.kind,
      src: asset.image,
      source: 'Material bank',
      file: asset.name,
    }));
  function toggle(item: PickedItem) {
    if (!isAllowedKind(item.kind)) return;
    const toggledKey = pickedItemKey(item);
    const alreadyPicked = picked.some((pickedItem) => pickedItemKey(pickedItem) === toggledKey);
    if (alreadyPicked) {
      setPicked(picked.filter((pickedItem) => pickedItemKey(pickedItem) !== toggledKey));
    } else {
      setPicked(multiple ? [...picked, item] : [item]);
    }
  }

  function onFiles(files: FileList | null) {
    setError('');
    if (!files) return;
    [...files].forEach((file) => {
      const kind = kindFromMimeType(file.type);
      if (!isAllowedKind(kind)) {
        setError(translate('{file} is not a supported type here.', { file: file.name }));
        return;
      }
      const isPreviewableInDemo = kind === 'Photo' && file.size <= MAX_DEMO_PREVIEW_BYTES;
      if (isPreviewableInDemo) {
        const reader = new FileReader();
        reader.onload = () =>
          setPicked((current) => {
            const item: PickedItem = {
              name: withoutExtension(file.name),
              kind,
              src: String(reader.result),
              source: 'Upload',
              file: file.name,
            };
            return multiple ? [...current, item] : [item];
          });
        reader.readAsDataURL(file);
      } else {
        setPicked((current) => {
          const referenceOnlyItem: PickedItem = {
            name: withoutExtension(file.name),
            kind,
            source: 'Upload',
            file: file.name,
          };
          return multiple ? [...current, referenceOnlyItem] : [referenceOnlyItem];
        });
      }
    });
  }

  function linkDrivePicksToLibrary() {
    const fromDrive = picked.filter((item) => item.source === 'Drive');
    if (fromDrive.length)
      commands.useDriveFiles(
        route.workspaceId,
        fromDrive.map((item) => ({
          name: item.file,
          kind: item.kind,
          image: item.src,
          sizeMb: driveSizes.current[item.file] ?? DEFAULT_DRIVE_FILE_SIZE_MB,
        }))
      );
  }

  function finish() {
    linkDrivePicksToLibrary();
    onPick(picked);
  }

  const isPicked = (item: PickedItem) =>
    picked.some((pickedItem) => pickedItem.source === item.source && pickedItem.file === item.file);
  const renderTile = (item: PickedItem) => (
    <button
      key={`${item.source}-${item.file}`}
      className={`picker-tile ${isPicked(item) ? 'selected' : ''}`}
      disabled={!isAllowedKind(item.kind)}
      aria-pressed={isPicked(item)}
      onClick={() => toggle(item)}
    >
      <span className="picker-thumb">
        {item.src ? (
          <img
            src={item.src}
            alt=""
            className={item.src.startsWith('data:image/svg') ? 'contain' : undefined}
          />
        ) : (
          <SafeIcon name={iconForKind[item.kind] as never} size={26} />
        )}
      </span>
      <span className="picker-name">{item.name}</span>
      <span className="muted picker-meta">{translate(item.kind)}</span>
      {isPicked(item) && (
        <span className="picker-check">
          <SafeIcon name="FiCheck" size={14} />
        </span>
      )}
    </button>
  );

  return (
    <div
      className="drawer-backdrop picker-backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onCancel();
      }}
    >
      <section
        className="drawer material-picker"
        role="dialog"
        aria-modal="true"
        aria-labelledby="picker-title"
      >
        <div className="drawer-header">
          <div>
            <h2 id="picker-title">{translate(title)}</h2>
            <div className="muted">
              {translate(multiple ? 'Choose one or more files' : 'Choose one file')}
            </div>
          </div>
          <button className="icon-button" onClick={onCancel} aria-label={translate('Close')}>
            <SafeIcon name="FiX" />
          </button>
        </div>
        <div className="picker-tabs" role="tablist">
          {(
            [
              ['upload', 'FiUpload', 'Upload'],
              ['bank', 'FiImage', 'Material bank'],
              ['drive', 'FiHardDrive', 'Google Drive'],
            ] as const
          ).map(([id, iconName, label]) => (
            <button
              key={id}
              role="tab"
              aria-selected={tab === id}
              className={`picker-tab ${tab === id ? 'selected' : ''}`}
              onClick={() => setTab(id)}
            >
              <SafeIcon name={iconName} size={15} />
              {translate(label)}
            </button>
          ))}
        </div>

        {tab === 'upload' && (
          <div className="picker-upload">
            <input
              ref={inputRef}
              type="file"
              multiple={multiple}
              hidden
              accept={
                kinds?.includes('Video') || !kinds
                  ? 'image/*,video/*,audio/*,.pdf,.heic,.heif,.dng'
                  : 'image/*,.pdf,.heic,.heif,.svg'
              }
              onChange={(event) => onFiles(event.target.files)}
            />
            <button
              className="picker-drop"
              onClick={() => inputRef.current?.click()}
              onDragOver={(event) => event.preventDefault()}
              onDrop={(event) => {
                event.preventDefault();
                onFiles(event.dataTransfer.files);
              }}
            >
              <SafeIcon name="FiUploadCloud" size={30} />
              <strong>{translate('Choose from your phone or computer')}</strong>
              <span className="muted">
                {translate(
                  'Photos, videos, audio and documents. No small size limits: AMY keeps an optimised copy for your posts.'
                )}
              </span>
            </button>
            <p className="muted small-print">
              {translate(
                'Demo: only small photos are previewed in this browser; nothing is uploaded to a server.'
              )}
            </p>
            {error && (
              <p className="field-error" role="alert">
                {error}
              </p>
            )}
            <div className="picker-grid">
              {picked.filter((item) => item.source === 'Upload').map(renderTile)}
            </div>
          </div>
        )}

        {tab === 'bank' && (
          <div className="picker-grid">
            {materialBankItems.length ? (
              materialBankItems.map(renderTile)
            ) : (
              <p className="muted">{translate('The Material bank is empty.')}</p>
            )}
          </div>
        )}

        {tab === 'drive' &&
          (drive.connected && drive.access === 'ok' ? (
            <>
              <DriveFolderView
                workspaceId={route.workspaceId}
                root={drive.folder}
                kinds={kinds}
                selected={picked
                  .filter((pickedItem) => pickedItem.source === 'Drive')
                  .map((pickedItem) => pickedItem.file)}
                onToggle={(file: DriveFile) => {
                  driveSizes.current[file.name] = file.sizeMb;
                  toggle({
                    name: withoutExtension(file.name),
                    kind: file.kind,
                    src: file.image,
                    source: 'Drive',
                    file: file.name,
                  });
                }}
              />
              <p className="muted small-print">
                {translate(
                  'Files stay in your Drive. AMY only prepares a copy of the files you use.'
                )}
              </p>
            </>
          ) : (
            <div className="panel calendar-empty">
              <SafeIcon name="FiHardDrive" />
              <p>
                {translate(
                  drive.connected
                    ? 'AMY can no longer open the linked Drive folder. Fix access in the Material bank.'
                    : 'Google Drive is not connected for this workspace. Connect a folder in the Material bank.'
                )}
              </p>
            </div>
          ))}

        <div className="picker-footer">
          <span className="muted">
            {picked.length
              ? translate('Selected: {n}', { n: picked.length })
              : translate('Nothing selected yet')}
          </span>
          <div className="button-row">
            <button className="button" onClick={onCancel}>
              {translate('Cancel')}
            </button>
            <button className="button primary" disabled={!picked.length} onClick={finish}>
              <SafeIcon name="FiCheck" />
              {translate('Use selected')}
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
